import { RequestTypes } from './requestTypes'
import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import {
  APIAxiosInstance,
  headersWithAccessToken,
} from '@core/utils/api-axios-instance'
import { uploadTemplateAnswerFiles } from '@core/react-query/features/permits/permit/helpers'
import { attachAuditResultMediaTypes } from '@core/entities/audit/AuditResult'

const checklistQueryKeys = createQueryKeys('checklist', {
  byId: ({ checklistId, accessToken }: RequestTypes['getChecklist']) => {
    return {
      queryKey: [checklistId],
      queryFn: async () => {
        const headers = headersWithAccessToken({ accessToken })
        const { data } = await APIAxiosInstance.get(
          `checklists/${checklistId}`,
          { headers: headers },
        )
        return data
      },
      contextQueries: {
        linkedProcesses: {
          queryKey: null,
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `checklists/${checklistId}/linked-processes`,
            )
            return data
          },
        },
        linkedTasks: {
          queryKey: null,
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `checklists/${checklistId}/tasks`,
            )

            return data
          },
        },
        timeline: {
          queryKey: null,
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `audits/checklists?checklistId=${checklistId}`,
            )
            await Promise.all(data.auditList.map(attachAuditResultMediaTypes))
            return data.auditList
          },
        },
        audits: {
          queryKey: null,
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(`audits/checklists`, {
              params: { checklistId },
            })
            await Promise.all(data.auditList.map(attachAuditResultMediaTypes))

            return data.auditList
          },
        },
        auditsByParticipantId: ({
          participantId,
        }: RequestTypes['getChecklistAuditsByParticipantId']) => ({
          queryKey: [participantId],
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `audits/checklists/${checklistId}/participants/${participantId}/all`,
            )
            await Promise.all(data.auditList.map(attachAuditResultMediaTypes))

            return data.auditList
          },
        }),
        getRoles: {
          queryKey: null,
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `checklists/${checklistId}/roles`,
              { headers: headersWithAccessToken({ accessToken }) },
            )

            return data.roles
          },
        },
      },
    }
  },
  draftById: ({ draftId }: RequestTypes['getChecklistDraft']) => {
    return {
      queryKey: [draftId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `checklists/drafts/${draftId}`,
        )
        return data
      },
    }
  },
  templateById: ({
    templateId,
    projectId,
  }: RequestTypes['getChecklistTemplate']) => {
    return {
      queryKey: [templateId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `checklists/templates/${templateId}?projectId=${projectId}`,
        )
        return data
      },
    }
  },
})

const checklistMutationKeys = createMutationKeys('checklist', {
  create: {
    mutationKey: null,
    mutationFn: async ({
      projectId,
      mainAnswersWrapper,
      mainAnswers,
      offlineSubmittedOn,
      idempotencyKey,
      onSubmissionProgress,
      signal,
      ...mutation
    }: RequestTypes['create']) => {
      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: mainAnswers?.answers || mainAnswersWrapper.answers,
        templateId: mutation.templateId,
        onSubmissionProgress,
        signal,
      })

      const { data } = await APIAxiosInstance.post(
        `checklists?projectId=${projectId}`,
        {
          ...mutation,
          mainAnswersWrapper: {
            trackingId: mainAnswersWrapper.trackingId,
            answers: uploadedAnswers,
            wrapperSessionTracking: mainAnswersWrapper.wrapperSessionTracking,
          },
          ...(mainAnswers && {
            mainAnswers: {
              answers: uploadedAnswers,
            },
          }),
        },
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
          signal,
        },
      )
      onSubmissionProgress?.(1)
      return data
    },
  },
  createDraft: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      mainAnswersWrapper,
      mainAnswers,
      offlineSubmittedOn,
      ...mutation
    }: RequestTypes['createDraft']) => {
      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: mainAnswers?.answers || mainAnswersWrapper?.answers || [],
        templateId: mutation.templateId,
      })

      const { data } = await APIAxiosInstance.post(
        `checklists/drafts?organisationId=${organisationId}`,
        {
          ...mutation,
          mainAnswersWrapper: {
            trackingId: mainAnswersWrapper?.trackingId,
            answers: uploadedAnswers,
            wrapperSessionTracking: mainAnswersWrapper?.wrapperSessionTracking,
          },
          ...(mainAnswers && {
            mainAnswers: {
              answers: uploadedAnswers,
            },
          }),
        },
        { headers: { 'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn } },
      )

      return data
    },
  },
  updateDraft: {
    mutationKey: null,
    mutationFn: async ({
      id,
      mainAnswersWrapper,
      mainAnswers,
      offlineSubmittedOn,
      ...mutation
    }: RequestTypes['updateDraft']) => {
      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: mainAnswers?.answers || mainAnswersWrapper?.answers || [],
        templateId: mutation.templateId,
      })

      const { data } = await APIAxiosInstance.put(
        `checklists/drafts/${id}`,
        {
          ...mutation,
          mainAnswersWrapper: {
            trackingId: mainAnswersWrapper?.trackingId,
            answers: uploadedAnswers,
            wrapperSessionTracking: mainAnswersWrapper?.wrapperSessionTracking,
          },
          ...(mainAnswers && {
            mainAnswers: {
              answers: uploadedAnswers,
            },
          }),
        },
        { headers: { 'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn } },
      )

      return data
    },
  },
  migrateDraftToLatestVersion: {
    mutationKey: null,
    mutationFn: async ({ id }: RequestTypes['migrateDraftToLatestVersion']) => {
      const { data } = await APIAxiosInstance.post(
        `checklists/drafts/${id}/migrate-to-latest-version`,
        { id },
      )

      return data
    },
  },
  deleteDraft: {
    mutationKey: null,
    mutationFn: async ({ draftId }: RequestTypes['deleteDraft']) => {
      const { data } = await APIAxiosInstance.delete(
        `checklists/drafts/${draftId}`,
      )

      return data
    },
  },
  clone: {
    mutationKey: null,
    mutationFn: async ({ checklistId }: RequestTypes['clone']) => {
      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/clone`,
        {},
      )
      return data
    },
  },
  removeParticipant: {
    mutationKey: null,
    mutationFn: async ({
      checklistId,
      participantId,
    }: RequestTypes['removeParticipant']) => {
      const { data } = await APIAxiosInstance.delete(
        `checklists/${checklistId}/participants/${participantId}`,
      )

      return data
    },
  },
  pdfDownloadUrl: {
    mutationKey: null,
    mutationFn: async ({ checklistId }: RequestTypes['getPdfDownloadUrl']) => {
      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/download`,
      )

      return data
    },
  },
  approve: {
    mutationKey: null,
    mutationFn: async (mutation: RequestTypes['approve']) => {
      const {
        checklistId,
        templateId,
        approvalId,
        approvalAnswers,
        idempotencyKey,
        onSubmissionProgress,
        signal,
      } = mutation

      const uploadedAnswers = approvalAnswers
        ? await uploadTemplateAnswerFiles({
            answers: approvalAnswers.answers,
            templateId,
            onSubmissionProgress,
            signal,
          })
        : undefined

      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/approve`,
        {
          approvalId,
          ...(uploadedAnswers
            ? { approvalAnswers: { answers: uploadedAnswers } }
            : {}),
        },
        {
          headers: { 'X-IDEMPOTENCY-KEY': idempotencyKey },
          signal,
        },
      )

      onSubmissionProgress?.(1)

      return data
    },
  },
  reject: {
    mutationKey: null,
    mutationFn: async (mutation: RequestTypes['reject']) => {
      const { checklistId, reason, approvalId } = mutation
      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/reject`,
        { reason, approvalId },
      )

      return data
    },
  },
  requestChanges: {
    mutationKey: null,
    mutationFn: async (mutation: RequestTypes['requestChanges']) => {
      const { checklistId, comment, approvalId, idempotencyKey } = mutation
      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/request-changes`,
        { comment, approvalId },
        idempotencyKey
          ? { headers: { 'X-IDEMPOTENCY-KEY': idempotencyKey } }
          : undefined,
      )

      return data
    },
  },
  addressChanges: {
    mutationKey: null,
    mutationFn: async ({
      offlineSubmittedOn,
      idempotencyKey,
      onSubmissionProgress,
      signal,
      ...mutation
    }: RequestTypes['addressChanges']) => {
      const { checklistId, templateId, mainAnswersWrapper, ...rest } = mutation

      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: mainAnswersWrapper.answers,
        templateId,
        onSubmissionProgress,
        signal,
      })

      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/address-requested-changes`,
        {
          ...rest,
          mainAnswersWrapper: {
            trackingId: mainAnswersWrapper.trackingId,
            answers: uploadedAnswers,
            wrapperSessionTracking: mainAnswersWrapper.wrapperSessionTracking,
          },
        },
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
          signal,
        },
      )

      onSubmissionProgress?.(1)

      return data
    },
  },
  nominateApprovers: {
    mutationKey: null,
    mutationFn: async (mutation: RequestTypes['nominateApprovers']) => {
      const { checklistId, approvalId, approvers } = mutation
      const { data } = await APIAxiosInstance.post(
        `checklists/${checklistId}/nominate-approvers`,
        { approvalId, approvers },
      )

      return data
    },
  },
})

export const checklistKeys = mergeQueryKeys(
  checklistQueryKeys,
  checklistMutationKeys,
).checklist
